import { Value } from 'baseui/select';
import { useFormState } from 'components/elements/form';
import TableEditableComponent from 'components/elements/table/table';
import {
  ActionType,
  ColumnDataType,
  IColumn,
} from 'components/elements/table/type';
import useGetVisaColumn from 'hooks/use-get-visa-column';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { DocumentPurchaseItemGuestDetailFormType } from '../form-type';

interface Props {
  parentIndex: number;
  guestIndex: number;
}

function useVisaDetailTableList(): IColumn<DocumentPurchaseItemGuestDetailFormType>[] {
  const { t } = useTranslation();
  const visaColumn = useGetVisaColumn();

  // const [visaTypeOptions, setVisaTypeOptions] = React.useState<Option[]>([]);
  // const [isVisaOptionsInitiate, setVisaOptionsIsInitiate] =
  //   React.useState<boolean>(false);

  // const { data } = useGetVisaDocuments();
  // React.useEffect(() => {
  //   if (!isVisaOptionsInitiate && data?.data) {
  //     const options = data.data.map((item) => visaOptionsTransformer(item));
  //     setVisaTypeOptions(options);
  //     setVisaOptionsIsInitiate(true);
  //   }
  // }, [data, isVisaOptionsInitiate]);

  const columns = React.useMemo<
    IColumn<DocumentPurchaseItemGuestDetailFormType>[]
  >(() => {
    return [
      {
        header: t('common:visa'),
        accessorKey: 'visaDocumentId',
        minSize: 400,
        dataType: ColumnDataType.select,
        selectProps: visaColumn,
      },
      {
        header: t('document:biometric'),
        accessorKey: 'biometric',
        minSize: 100,
        dataType: ColumnDataType.checkbox,
      },
      {
        header: t('document:appointment_date'),
        accessorKey: 'appointmentAt',
        minSize: 100,
        dataType: ColumnDataType.date,
        required: true,
      },
      {
        header: t('common:description'),
        accessorKey: 'description',
        minSize: 300,
        dataType: ColumnDataType.text,
      },
      {
        header: t('common:price'),
        accessorKey: 'price',
        minSize: 100,
        dataType: ColumnDataType.number,
        minNumber: 0,
        required: true,
      },
    ];
  }, [t, visaColumn]);

  return columns;
}

const VisaDetailTable = (props: Props) => {
  const { guestIndex, parentIndex } = props;
  const { editable } = useFormState();
  const { clearErrors, control, getValues } = useFormContext();
  const columns = useVisaDetailTableList();

  const name =
    `documentPurchases.${parentIndex}.documentPurchaseGuests.${guestIndex}.details` as const;

  const details = useWatch({
    control,
    name,
  });

  const { update } = useFieldArray({
    control,
    name,
    keyName: '__customId',
  });

  const dispatch = React.useCallback(
    (action) => {
      const details = getValues(name);
      const currentActionRow = action.rowIndex as number;
      const detail = details[currentActionRow];

      const type = action.type as ActionType;
      const columnId =
        action.columnId as keyof DocumentPurchaseItemGuestDetailFormType;
      const extra = action.extra as Value;
      const value = action.value;

      const otherVal: () => Partial<DocumentPurchaseItemGuestDetailFormType> =
        () => {
          if (columnId === 'visaDocumentId') {
            const price = extra?.[0]?.price as number | undefined;
            const visaDocumentId = extra?.[0]?.id as string | undefined;
            const label = extra?.[0]?.label as string | undefined;
            return {
              price: price || 0,
              visaDocumentId,
              label,
            };
          }
          return {};
        };

      if (type === 'update_cell') {
        update(currentActionRow, {
          ...detail,
          [columnId]: value,
          ...otherVal(),
        });
        clearErrors(`${name}.${currentActionRow}`);
      }
    },
    [clearErrors, getValues, name, update],
  );

  return (
    <div style={{ width: '100%' }}>
      <TableEditableComponent
        columns={columns}
        data={(details || []) as DocumentPurchaseItemGuestDetailFormType[]}
        dispatch={dispatch}
        disabled={!editable}
        noAddRemoveRow
        name={name}
        uniqueRowKey="customId"
        onCustomDisabledCell={({ accessorKey, rowOriginal }) => {
          if (!rowOriginal.biometric && accessorKey === 'appointmentAt') {
            return true;
          }
          return false;
        }}
      />
    </div>
  );
};

export default VisaDetailTable;
